import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SnackbarService } from '@bcl/design-system';
import {
  VisitService,
  VisitAreasModulesModel,
  VisitAreasModulesModuleItemModel,
} from 'src/app/services/visit.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

export interface FollowUpAreasModulesDialogData {
  id: number;
  currentSelections?: VisitAreasModulesModel[];
}

export interface ExtendedVisitAreasModulesModuleItemModel
  extends VisitAreasModulesModuleItemModel {
  originallyDisabled?: boolean;
}

export interface ExtendedVisitAreasModulesModel
  extends Omit<VisitAreasModulesModel, 'modules'> {
  modules?: ExtendedVisitAreasModulesModuleItemModel[];
}

export interface FollowUpAreasModulesDialogResult {
  selectedAreasModules: VisitAreasModulesModel[];
}

@Component({
  selector: 'app-follow-up-areas-modules-dialog',
  templateUrl: './follow-up-areas-modules-dialog.component.html',
  styleUrls: ['./follow-up-areas-modules-dialog.component.scss'],
})
export class FollowUpAreasModulesDialogComponent implements OnInit {
  public areasModules: ExtendedVisitAreasModulesModel[] = [];
  public originalAreasModules: ExtendedVisitAreasModulesModel[] = [];
  public form: FormGroup;
  public isLoading = true;
  public isSaving = false;
  public uniqueModuleNames: string[] = [];
  public moduleSelectOptions: { label: string; value: string }[] = [];
  private areaIndexMapping: { [displayIndex: number]: number } = {};
  private moduleIndexMapping: {
    [areaDisplayIndex: number]: { [moduleDisplayIndex: number]: number };
  } = {};

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA)
    public readonly data: FollowUpAreasModulesDialogData,
    private readonly dialogRef: MatDialogRef<
      FollowUpAreasModulesDialogComponent,
      FollowUpAreasModulesDialogResult
    >,
    private readonly visitService: VisitService,
    private readonly snackbarService: SnackbarService
  ) {
    this.form = this.fb.group({});
  }

  ngOnInit(): void {
    this.loadAreasModules();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public getAreaFormControl(areaIndex: number): any {
    const controlName = `area_${areaIndex}`;
    return this.form.get(controlName);
  }

  public getModuleFormControl(areaIndex: number, moduleIndex: number): any {
    const controlName = `area_${areaIndex}_module_${moduleIndex}`;
    return this.form.get(controlName);
  }

  public onModuleToggle(
    areaIndex: number,
    moduleIndex: number,
    checked: boolean
  ): void {
    const areaControlName = `area_${areaIndex}`;
    const areaControl = this.form.get(areaControlName);
    if (areaControl) {
      const isAreaSelected = this.isAreaSelected(areaIndex);
      const isAreaPartiallySelected = this.isAreaPartiallySelected(areaIndex);

      if (isAreaSelected) {
        areaControl.setValue(true, { emitEvent: false });
      } else if (isAreaPartiallySelected) {
        areaControl.setValue(false, { emitEvent: false });
      } else {
        areaControl.setValue(false, { emitEvent: false });
      }
    }
  }

  public onAreaToggle(areaIndex: number, checked: boolean): void {
    const area = this.areasModules[areaIndex];
    if (area.modules) {
      area.modules.forEach((module, moduleIndex) => {
        const controlName = `area_${areaIndex}_module_${moduleIndex}`;
        this.form.get(controlName)?.setValue(checked, { emitEvent: false });
      });
    }
  }

  public isAreaSelected(areaIndex: number): boolean {
    const area = this.areasModules[areaIndex];
    if (!area.modules || area.modules.length === 0) {
      return false;
    }
    return area.modules.every((module, moduleIndex) => {
      const controlName = `area_${areaIndex}_module_${moduleIndex}`;
      return this.form.get(controlName)?.value === true;
    });
  }

  public isAreaPartiallySelected(areaIndex: number): boolean {
    const area = this.areasModules[areaIndex];
    if (!area.modules || area.modules.length === 0) {
      return false;
    }
    const selectedCount = area.modules.filter((module, moduleIndex) => {
      const controlName = `area_${areaIndex}_module_${moduleIndex}`;
      return this.form.get(controlName)?.value === true;
    }).length;
    return selectedCount > 0 && selectedCount < area.modules.length;
  }

  public getSelectedModulesCount(): number {
    let count = 0;
    this.areasModules.forEach((area, areaIndex) => {
      if (area.modules) {
        area.modules.forEach((module, moduleIndex) => {
          const controlName = `area_${areaIndex}_module_${moduleIndex}`;
          if (this.form.get(controlName)?.value === true) {
            count++;
          }
        });
      }
    });
    return count;
  }

  public getTotalModulesCount(): number {
    let count = 0;
    this.areasModules.forEach((area) => {
      if (area.modules) {
        count += area.modules.length;
      }
    });
    return count;
  }

  public save(): void {
    if (this.isSaving) {
      return;
    }

    this.isSaving = true;

    const formValue = this.form.value;

    this.areasModules.forEach((displayArea, displayAreaIndex) => {
      const originalAreaIndex = this.areaIndexMapping[displayAreaIndex];
      const originalArea = this.originalAreasModules[originalAreaIndex];

      if (originalArea && originalArea.modules) {
        const areaControlName = `area_${displayAreaIndex}`;
        const isAreaSelected = formValue[areaControlName] || false;

        originalArea.modules.forEach((module, originalModuleIndex) => {
          const moduleMapping = this.moduleIndexMapping[displayAreaIndex];
          const displayModuleIndex = Object.keys(moduleMapping).find(
            (key) => moduleMapping[parseInt(key, 10)] === originalModuleIndex
          );

          if (displayModuleIndex !== undefined) {
            const controlName = `area_${displayAreaIndex}_module_${displayModuleIndex}`;
            const isSelected = formValue[controlName] || false;
            module.value = isSelected;
          } else {
            if (!isAreaSelected) {
              module.value = false;
            }
          }
        });
      }
    });

    const result: FollowUpAreasModulesDialogResult = {
      selectedAreasModules: this.originalAreasModules.map((area) => ({
        ...area,
        modules: area.modules?.map((module) => ({
          name: module.name,
          label: module.label,
          value: module.value,
          contractPartId: module.contractPartId,
        })),
      })),
    };

    this.dialogRef.close(result);
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  public selectAll(): void {
    this.areasModules.forEach((area, areaIndex) => {
      const areaControlName = `area_${areaIndex}`;
      this.form.get(areaControlName)?.setValue(true, { emitEvent: true });
    });
  }

  public deselectAll(): void {
    this.areasModules.forEach((area, areaIndex) => {
      const areaControlName = `area_${areaIndex}`;
      this.form.get(areaControlName)?.setValue(false, { emitEvent: true });
    });
  }

  public onModuleNameSelected(): void {
    const moduleName = this.form.get('moduleSelect')?.value;

    if (!moduleName) {
      return;
    }

    this.areasModules.forEach((area, areaIndex) => {
      if (area.modules) {
        area.modules.forEach((module, moduleIndex) => {
          if (module.name === moduleName) {
            const controlName = `area_${areaIndex}_module_${moduleIndex}`;
            this.form.get(controlName)?.setValue(true);
          }
        });

        const areaControlName = `area_${areaIndex}`;
        const areaControl = this.form.get(areaControlName);
        if (areaControl) {
          const isAreaSelected = this.isAreaSelected(areaIndex);
          const isAreaPartiallySelected =
            this.isAreaPartiallySelected(areaIndex);

          if (isAreaSelected) {
            areaControl.setValue(true, { emitEvent: false });
          } else if (isAreaPartiallySelected) {
            areaControl.setValue(false, { emitEvent: false });
          }
        }
      }
    });

    setTimeout(() => {
      this.form.get('moduleSelect')?.setValue(null, { emitEvent: false });
    }, 100);
  }

  private extractUniqueModuleNames(): void {
    const moduleNamesSet = new Set<string>();

    this.areasModules.forEach((area) => {
      if (area.modules) {
        area.modules.forEach((module) => {
          moduleNamesSet.add(module.name);
        });
      }
    });

    this.uniqueModuleNames = Array.from(moduleNamesSet).sort();

    this.moduleSelectOptions = this.uniqueModuleNames.map((name) => ({
      label: name,
      value: name,
    }));

    this.form
      .get('moduleSelect')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (value) {
          this.onModuleNameSelected();
        }
      });
  }

  private loadAreasModules(): void {
    this.visitService
      .getVisitAreasModules(this.data.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (areasModules) => {
          this.originalAreasModules =
            this.addOriginallyDisabledProperty(areasModules);

          if (
            this.data.currentSelections &&
            this.data.currentSelections.length > 0
          ) {
            this.applyCurrentSelections(this.data.currentSelections);
          }

          this.processAreasModules(this.originalAreasModules);
        },
        error: (error) => {
          console.error('Error loading areas and modules:', error);
          this.snackbarService.open(
            'red',
            'AREA.DETAILS.VISIT_SUMMARY.AREAS_MODULES_DIALOG.LOAD_ERROR'
          );
          this.isLoading = false;
        },
      });
  }

  private addOriginallyDisabledProperty(
    areasModules: VisitAreasModulesModel[]
  ): ExtendedVisitAreasModulesModel[] {
    return areasModules.map((area) => ({
      ...area,
      modules: area.modules?.map((module) => ({
        ...module,
        originallyDisabled: module.value === false,
      })),
    }));
  }

  private applyCurrentSelections(
    currentSelections: VisitAreasModulesModel[]
  ): void {
    currentSelections.forEach((currentArea) => {
      const matchingArea = this.originalAreasModules.find(
        (area) => area.name === currentArea.name
      );
      if (matchingArea && matchingArea.modules && currentArea.modules) {
        currentArea.modules.forEach((currentModule) => {
          const matchingModule = matchingArea.modules!.find(
            (module) => module.name === currentModule.name
          );
          if (matchingModule) {
            matchingModule.value = currentModule.value;
          }
        });
      }
    });
  }

  private wasAreaOriginallySelected(area: VisitAreasModulesModel): boolean {
    if (!this.data.currentSelections) {
      return false;
    }

    const matchingArea = this.data.currentSelections.find(
      (currentArea) => currentArea.name === area.name
    );

    return matchingArea !== undefined;
  }

  private processAreasModules(
    areasModules: ExtendedVisitAreasModulesModel[]
  ): void {
    this.areaIndexMapping = {};
    this.moduleIndexMapping = {};

    const filteredAreas: ExtendedVisitAreasModulesModel[] = [];
    let displayAreaIndex = 0;

    areasModules.forEach((area, originalAreaIndex) => {
      if (area.modules && area.modules.length > 0) {
        const shouldShowArea =
          this.data.currentSelections && this.data.currentSelections.length > 0
            ? this.wasAreaOriginallySelected(area)
            : area.modules.some((module) => module.value === true);

        if (shouldShowArea) {
          this.areaIndexMapping[displayAreaIndex] = originalAreaIndex;
          this.moduleIndexMapping[displayAreaIndex] = {};

          const modulesToShow =
            this.data.currentSelections &&
            this.data.currentSelections.length > 0
              ? area.modules.filter((module) => !module.originallyDisabled)
              : area.modules.filter((module) => module.value === true);

          let displayModuleIndex = 0;
          area.modules.forEach((module, originalModuleIndex) => {
            if (modulesToShow.includes(module)) {
              this.moduleIndexMapping[displayAreaIndex][displayModuleIndex] =
                originalModuleIndex;
              displayModuleIndex++;
            }
          });

          filteredAreas.push({
            ...area,
            modules: modulesToShow,
          });
          displayAreaIndex++;
        }
      }
    });

    this.areasModules = filteredAreas;

    this.buildForm();
    this.extractUniqueModuleNames();
    this.isLoading = false;
  }

  private buildForm(): void {
    const formControls: { [key: string]: any } = {};

    formControls.moduleSelect = [''];

    const hasCurrentSelections =
      this.data.currentSelections && this.data.currentSelections.length > 0;

    this.areasModules.forEach((area, areaIndex) => {
      const areaControlName = `area_${areaIndex}`;

      if (hasCurrentSelections && area.modules) {
        const isAreaSelected = area.modules.every(
          (module) => module.value === true
        );
        formControls[areaControlName] = [isAreaSelected];

        area.modules.forEach((module, moduleIndex) => {
          const controlName = `area_${areaIndex}_module_${moduleIndex}`;
          formControls[controlName] = [module.value || false];
        });
      } else {
        formControls[areaControlName] = [false];
        if (area.modules) {
          area.modules.forEach((module, moduleIndex) => {
            const controlName = `area_${areaIndex}_module_${moduleIndex}`;
            formControls[controlName] = [false];
          });
        }
      }
    });

    this.form = this.fb.group(formControls);

    this.setupFormSubscriptions();
  }

  private setupFormSubscriptions(): void {
    this.areasModules.forEach((area, areaIndex) => {
      const areaControlName = `area_${areaIndex}`;
      const areaControl = this.form.get(areaControlName);

      if (areaControl) {
        areaControl.valueChanges
          .pipe(takeUntil(this.destroy$))
          .subscribe((checked: boolean) => {
            this.onAreaToggle(areaIndex, checked);
          });
      }

      if (area.modules) {
        area.modules.forEach((module, moduleIndex) => {
          const controlName = `area_${areaIndex}_module_${moduleIndex}`;
          const moduleControl = this.form.get(controlName);

          if (moduleControl) {
            moduleControl.valueChanges
              .pipe(takeUntil(this.destroy$))
              .subscribe((checked: boolean) => {
                this.onModuleToggle(areaIndex, moduleIndex, checked);
              });
          }
        });
      }
    });
  }
}
